<script lang="ts">
  /**
   * SettingsOverlay — game settings + API key management.
   */
  import { settingsStore } from "$lib/stores/game.svelte";
  import type { AnimationLevel } from "$lib/stores/game.svelte";
  import { storeApiKey, getApiKey, deleteApiKey, validateApiKey } from "$lib/api/invoke";
  import { keyCache } from "$lib/stores/keys";
  import { clearFirstRun } from "$lib/stores/firstRun";

  const PROVIDERS = ["gemini", "claude", "openai", "fireworks", "deepseek", "together"] as const;

  let localWpm = $state(settingsStore.wpm);
  let keyInputs: Record<string, string> = $state({});
  let keyStatuses: Record<string, { is_set: boolean; masked: string }> = $state({});
  let validating = $state("");
  let statusMessage = $state("");

  async function loadKeyStatuses(): Promise<void> {
    for (const provider of PROVIDERS) {
      try {
        const info = await getApiKey(provider);
        keyStatuses[provider] = { is_set: info.is_set, masked: info.masked };
      } catch {
        keyStatuses[provider] = { is_set: false, masked: "" };
      }
    }
  }

  // Load statuses on mount.
  loadKeyStatuses();

  function applyWpm(): void {
    settingsStore.wpm = localWpm;
  }

  function toggleInputMode(): void {
    settingsStore.inputMode = settingsStore.inputMode === "guided" ? "freeform" : "guided";
  }

  function toggleRulesMode(): void {
    settingsStore.rulesMode = settingsStore.rulesMode === "strict" ? "loose" : "strict";
  }

  function cycleAnimation(): void {
    const order: AnimationLevel[] = ["prominent", "subtle", "off"];
    const idx = order.indexOf(settingsStore.animationLevel);
    settingsStore.animationLevel = order[(idx + 1) % order.length];
  }

  function restoreDefaults(): void {
    settingsStore.reset();
    localWpm = 350;
    statusMessage = "Settings restored to defaults.";
  }

  function clearAll(): void {
    localStorage.clear();
    window.location.href = "/";
  }

  function previewOobe(): void {
    clearFirstRun();
    window.location.reload();
  }

  function wpmBar(value: number): string {
    const pct = Math.round(((value - 100) / 500) * 20);
    return "[" + "=".repeat(pct) + ".".repeat(20 - pct) + "]";
  }

  function glyphSpeedBar(value: number): string {
    const pct = Math.round(((value - 100) / 400) * 20);
    return "[" + "=".repeat(pct) + ".".repeat(20 - pct) + "]";
  }

  function glyphDensityBar(value: number): string {
    const pct = Math.round(((value - 5) / 45) * 20);
    return "[" + "=".repeat(pct) + ".".repeat(20 - pct) + "]";
  }

  async function saveKey(provider: string): Promise<void> {
    const key = keyInputs[provider]?.trim();
    if (!key) return;

    validating = provider;
    statusMessage = "";

    try {
      const valid = await validateApiKey(provider, key);
      if (!valid) {
        statusMessage = `Invalid ${provider} key format.`;
        validating = "";
        return;
      }
      await storeApiKey(provider, key);
      await keyCache.store(provider, key);
      keyStatuses[provider] = {
        is_set: true,
        masked: key.length > 8 ? key.slice(0, 4) + "..." + key.slice(-4) : "****",
      };
      keyInputs[provider] = "";
      statusMessage = `${provider} key saved.`;
    } catch (e) {
      statusMessage = `Error: ${e}`;
    }
    validating = "";
  }

  async function removeKey(provider: string): Promise<void> {
    try {
      await deleteApiKey(provider);
      keyCache.invalidate(provider);
      keyStatuses[provider] = { is_set: false, masked: "" };
      statusMessage = `${provider} key removed.`;
    } catch (e) {
      statusMessage = `Error: ${e}`;
    }
  }
</script>

<div class="settings-overlay">
  <h2 class="settings-overlay__title">SETTINGS (?)</h2>

  <div class="settings-overlay__border">+{"=".repeat(44)}+</div>

  <div class="settings-overlay__options">
    <!-- WPM -->
    <div class="settings-overlay__row">
      <span class="settings-overlay__label">Typewriter Speed</span>
      <div class="settings-overlay__control">
        <button
          class="settings-overlay__btn"
          onclick={() => {
            localWpm = Math.max(100, localWpm - 50);
            applyWpm();
          }}
        >
          [-]
        </button>
        <span class="settings-overlay__value">{localWpm} WPM</span>
        <button
          class="settings-overlay__btn"
          onclick={() => {
            localWpm = Math.min(600, localWpm + 50);
            applyWpm();
          }}
        >
          [+]
        </button>
      </div>
      <span class="settings-overlay__bar">{wpmBar(localWpm)}</span>
    </div>

    <!-- Input Mode -->
    <div class="settings-overlay__row">
      <span class="settings-overlay__label">Input Mode</span>
      <button class="settings-overlay__btn-toggle" onclick={toggleInputMode}>
        [{settingsStore.inputMode === "guided" ? "GUIDED" : "FREEFORM"}]
      </button>
    </div>

    <!-- Rules Mode -->
    <div class="settings-overlay__row">
      <span class="settings-overlay__label">Rules Mode</span>
      <button class="settings-overlay__btn-toggle" onclick={toggleRulesMode}>
        [{settingsStore.rulesMode === "strict" ? "STRICT" : "LOOSE"}]
      </button>
    </div>

    <!-- Animation -->
    <div class="settings-overlay__row">
      <span class="settings-overlay__label">Animation</span>
      <button class="settings-overlay__btn-toggle" onclick={cycleAnimation}>
        [{settingsStore.animationLevel.toUpperCase()}]
      </button>
    </div>

    <!-- Glyph Speed (only when animation is not off) -->
    {#if settingsStore.animationLevel !== "off"}
      <div class="settings-overlay__row">
        <span class="settings-overlay__label">Glyph Speed</span>
        <div class="settings-overlay__control">
          <button
            class="settings-overlay__btn"
            onclick={() => {
              settingsStore.glyphSpeedMs = Math.max(100, settingsStore.glyphSpeedMs - 50);
            }}
          >
            [-]
          </button>
          <span class="settings-overlay__value">{settingsStore.glyphSpeedMs}ms</span>
          <button
            class="settings-overlay__btn"
            onclick={() => {
              settingsStore.glyphSpeedMs = Math.min(500, settingsStore.glyphSpeedMs + 50);
            }}
          >
            [+]
          </button>
        </div>
        <span class="settings-overlay__bar">{glyphSpeedBar(settingsStore.glyphSpeedMs)}</span>
      </div>

      <!-- Glyph Density -->
      <div class="settings-overlay__row">
        <span class="settings-overlay__label">Glyph Density</span>
        <div class="settings-overlay__control">
          <button
            class="settings-overlay__btn"
            onclick={() => {
              settingsStore.glyphDensity = Math.max(5, settingsStore.glyphDensity - 5);
            }}
          >
            [-]
          </button>
          <span class="settings-overlay__value">{settingsStore.glyphDensity}%</span>
          <button
            class="settings-overlay__btn"
            onclick={() => {
              settingsStore.glyphDensity = Math.min(50, settingsStore.glyphDensity + 5);
            }}
          >
            [+]
          </button>
        </div>
        <span class="settings-overlay__bar">{glyphDensityBar(settingsStore.glyphDensity)}</span>
      </div>
    {/if}

    <!-- API Keys -->
    <div class="settings-overlay__section">
      <span class="settings-overlay__label">API Keys</span>
      <p class="settings-overlay__hint">
        Get a free Gemini key at Google AI Studio. Claude, OpenAI, Fireworks, DeepSeek, and Together
        also supported.
      </p>

      {#each PROVIDERS as provider}
        <div class="settings-overlay__key-row">
          <span class="settings-overlay__key-name">{provider}</span>
          {#if keyStatuses[provider]?.is_set}
            <span class="settings-overlay__key-masked">{keyStatuses[provider].masked}</span>
            <button class="settings-overlay__btn" onclick={() => removeKey(provider)}>[x]</button>
          {:else}
            <input
              class="settings-overlay__key-input"
              type="password"
              placeholder="paste key..."
              bind:value={keyInputs[provider]}
              onkeydown={(e: KeyboardEvent) => {
                if (e.key === "Enter") saveKey(provider);
              }}
            />
            <button
              class="settings-overlay__btn"
              disabled={validating === provider}
              onclick={() => saveKey(provider)}
              >{validating === provider ? "[...]" : "[save]"}</button
            >
          {/if}
        </div>
      {/each}

      {#if statusMessage}
        <p class="settings-overlay__status">{statusMessage}</p>
      {/if}
    </div>
  </div>

  <!-- System Actions -->
  <div class="settings-overlay__section settings-overlay__section--actions">
    <span class="settings-overlay__label">System</span>
    <div class="settings-overlay__action-row">
      <button class="settings-overlay__btn-toggle" onclick={restoreDefaults}>
        [Restore Defaults]
      </button>
      <button class="settings-overlay__btn-toggle" onclick={previewOobe}> [Preview OOBE] </button>
      <button class="settings-overlay__btn-toggle settings-overlay__btn--danger" onclick={clearAll}>
        [Clear All / Reset]
      </button>
    </div>
    <p class="settings-overlay__hint">
      Restore Defaults resets all settings (not API keys). Preview OOBE shows the first-run wizard.
      Clear All wipes all data and returns to setup.
    </p>
  </div>

  <div class="settings-overlay__border">+{"=".repeat(44)}+</div>
</div>

<style>
  .settings-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
  }

  .settings-overlay__title {
    color: var(--fg-bright);
    font-size: 1.1rem;
    letter-spacing: 0.15em;
  }

  .settings-overlay__border {
    color: var(--fg-dim);
    font-size: 0.85rem;
  }

  .settings-overlay__options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
    max-width: 50ch;
  }

  .settings-overlay__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .settings-overlay__label {
    color: var(--fg);
    font-weight: bold;
    min-width: 18ch;
  }

  .settings-overlay__control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .settings-overlay__value {
    color: var(--fg-bright);
    min-width: 8ch;
    text-align: center;
  }

  .settings-overlay__bar {
    color: var(--fg-dim);
    font-size: 0.8rem;
  }

  .settings-overlay__btn,
  .settings-overlay__btn-toggle {
    background: none;
    border: none;
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.85rem;
    cursor: pointer;
    padding: 0.1rem 0.25rem;
  }

  .settings-overlay__btn:hover,
  .settings-overlay__btn-toggle:hover {
    color: var(--fg-bright);
  }

  .settings-overlay__btn:disabled {
    color: var(--fg-dim);
    cursor: wait;
  }

  .settings-overlay__section {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    border-top: 1px solid var(--fg-dim);
    padding-top: 0.75rem;
    margin-top: 0.25rem;
  }

  .settings-overlay__hint {
    color: var(--fg-dim);
    font-size: 0.8rem;
    line-height: 1.4;
  }

  .settings-overlay__key-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
  }

  .settings-overlay__key-name {
    color: var(--fg);
    min-width: 10ch;
    text-transform: capitalize;
  }

  .settings-overlay__key-masked {
    color: var(--fg-dim);
    font-size: 0.8rem;
  }

  .settings-overlay__key-input {
    background: var(--bg);
    border: 1px solid var(--fg-dim);
    color: var(--fg);
    font-family: var(--font-mono);
    font-size: 0.8rem;
    padding: 0.15rem 0.3rem;
    width: 20ch;
  }

  .settings-overlay__key-input:focus {
    border-color: var(--fg);
    outline: none;
  }

  .settings-overlay__status {
    color: var(--fg-bright);
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .settings-overlay__section--actions {
    border-top: 1px solid var(--fg-dim);
    padding-top: 0.75rem;
    margin-top: 0.25rem;
  }

  .settings-overlay__action-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }

  .settings-overlay__btn--danger {
    color: var(--accent, #ff4444);
  }

  .settings-overlay__btn--danger:hover {
    color: #ff6666;
  }
</style>
